import React, { useEffect, useState, ReactNode } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar/Sidebar';
import Header from '../Header/Header';
import { useAuth } from '../../contexts/AuthContext';
import {
  IconAreas,
  IconDashboard,
  IconMenu,
  IconUser,
  IconWallet,
} from '../icons/Icons';
import './AppLayout.css';

const SIDEBAR_COLLAPSED_KEY = 'flypix:sidebar-collapsed';

interface AppLayoutProps {
  children: ReactNode;
}

/**
 * Основной каркас личного кабинета: боковое меню + верхняя панель + контент.
 *
 * Layout только оборачивает страницы — маршруты, права доступа и данные
 * остаются в существующих компонентах без изменений.
 */
const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const { user, isImpersonating, stopImpersonating } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [returningAdmin, setReturningAdmin] = useState(false);
  const location = useLocation();

  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  const handleStopImpersonating = async () => {
    setReturningAdmin(true);
    try {
      await stopImpersonating();
      navigate('/admin');
    } catch (error) {
      console.error(error);
      navigate('/login');
    } finally {
      setReturningAdmin(false);
    }
  };

  // Меню закрывается при переходе на другой раздел
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Блокируем прокрутку страницы, пока открыт мобильный drawer
  useEffect(() => {
    if (!mobileOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!mobileOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen]);

  return (
    <div
      className={`app-shell${sidebarCollapsed ? ' app-shell--sidebar-collapsed' : ''}`}
    >
      <Sidebar
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
        collapsed={sidebarCollapsed}
        onToggleCollapsed={toggleSidebarCollapsed}
      />

      <div className="app-shell__main">
        {isImpersonating && (
          <div className="impersonation-banner" role="status">
            <span>
              Вы вошли как <strong>{user?.name || user?.email}</strong>
            </span>
            <button
              type="button"
              className="impersonation-banner__btn"
              disabled={returningAdmin}
              onClick={() => void handleStopImpersonating()}
            >
              {returningAdmin ? 'Возврат…' : 'Вернуться в админку'}
            </button>
          </div>
        )}

        <Header onOpenMenu={() => setMobileOpen(true)} />

        <main className="app-shell__content">{children}</main>
      </div>

      <nav className="mobile-nav" aria-label="Быстрая навигация">
        <NavLink
          to="/dashboard"
          className={({ isActive }) =>
            `mobile-nav__item${isActive ? ' mobile-nav__item--active' : ''}`
          }
        >
          <IconDashboard size={20} />
          <span>Дашборд</span>
        </NavLink>
        <NavLink
          to="/dashboard"
          className={`mobile-nav__item${
            location.pathname.startsWith('/area') ? ' mobile-nav__item--active' : ''
          }`}
        >
          <IconAreas size={20} />
          <span>Участки</span>
        </NavLink>
        <NavLink
          to="/balance"
          className={({ isActive }) =>
            `mobile-nav__item${isActive ? ' mobile-nav__item--active' : ''}`
          }
        >
          <IconWallet size={20} />
          <span>Баланс</span>
        </NavLink>
        <NavLink
          to="/profile"
          className={({ isActive }) =>
            `mobile-nav__item${isActive ? ' mobile-nav__item--active' : ''}`
          }
        >
          <IconUser size={20} />
          <span>Профиль</span>
        </NavLink>
        <button
          type="button"
          className="mobile-nav__item"
          onClick={() => setMobileOpen(true)}
        >
          <IconMenu size={20} />
          <span>Меню</span>
        </button>
      </nav>
    </div>
  );
};

export default AppLayout;
